'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  useGetListsQuery,
  useAddItemsToListMutation,
  useCreateListMutation,
  useDeleteListItemMutation,
} from 'src/api/redux';
import { Dialog, PulseButton, CircularProgress, Search } from 'src/components';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import useLists from 'src/hooks/lists/useLists';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';
import { ListSelector, CreateListInput } from './components';
import type {
  AddToListModalProps,
  ProviderItem,
  NormalizedProvider,
  AddToListResult,
} from './types';

/**
 * Normalize providers from various input formats to API-ready format
 */
const normalizeProviders = (
  providers: ProviderItem[]
): NormalizedProvider[] => {
  if (!providers || !Array.isArray(providers)) return [];

  return providers
    .filter((p): p is NonNullable<typeof p> => p != null)
    .map((provider) => {
      // Handle nested provider structure from selection context
      const base = provider.provider ?? provider;
      return {
        provider_id: String(base.provider_id ?? base.id),
        type: base.type || 'hcp',
        list_item_id: base.list_item_id,
      };
    });
};

/**
 * Unified modal for adding or moving providers to lists
 */
const AddToListModal: React.FC<AddToListModalProps> = ({
  isOpen,
  onClose,
  mode,
  providers,
  sourceList,
  onSuccess,
  isResolving = false,
}) => {
  const log = useLogging();
  const { user } = useAuth();
  const { setNotification } = useNotification();
  const { triggerListMembersRefetch } = useLists();

  // API hooks
  const { data: providerLists, isLoading: providerListsLoading } =
    useGetListsQuery({}, { skip: !isOpen });
  const [addItemsToList, { isLoading: isAdding }] = useAddItemsToListMutation();
  const [createList] = useCreateListMutation();
  const [deleteListItem] = useDeleteListItemMutation();

  // Local state
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const isProcessing = isAdding || progress.total > 0;

  // Normalize providers once
  const normalizedProviders = useMemo(
    () => normalizeProviders(providers),
    [providers]
  );

  // Group lists by owner
  const groupedLists = useMemo(() => {
    if (!providerLists?.results?.length) return { owned: [], shared: [] };

    const filtered = debouncedSearchTerm
      ? providerLists.results.filter((list: MedScout.List) =>
          list.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
      : providerLists.results;

    return filtered.reduce(
      (
        acc: { owned: MedScout.List[]; shared: MedScout.List[] },
        list: MedScout.List
      ) => {
        if (list.owner.id === user?.id) {
          acc.owned.push(list);
        } else {
          acc.shared.push(list);
        }
        return acc;
      },
      { owned: [], shared: [] }
    );
  }, [providerLists, user, debouncedSearchTerm]);

  const hasNoSearchResults =
    debouncedSearchTerm.trim().length > 0 &&
    groupedLists.owned.length === 0 &&
    groupedLists.shared.length === 0;

  const nameAlreadyExists = useMemo(() => {
    if (!newListName.trim() || !providerLists?.results) return false;
    return providerLists.results.some(
      (list: MedScout.List) =>
        list.name.toLowerCase() === newListName.trim().toLowerCase()
    );
  }, [newListName, providerLists]);

  // Filter out providers already in target list
  const filterDuplicates = useCallback(
    (targetListId: string): NormalizedProvider[] => {
      return normalizedProviders.filter((provider) => {
        const originalProvider = providers.find(
          (p) =>
            String(
              p.provider?.provider_id ?? p.provider?.id ?? p.provider_id ?? p.id
            ) === provider.provider_id
        );
        const providerLists =
          originalProvider?.provider?.provider_lists ??
          originalProvider?.provider_lists;

        if (!providerLists || providerLists.length === 0) return true;
        return !providerLists.some(
          (list) => list.id?.toString() === targetListId
        );
      });
    },
    [normalizedProviders, providers]
  );

  // Get selected list name
  const getSelectedListName = useCallback(
    (listId: string): string => {
      if (listId === 'new-list-id') return newListName;
      const list = providerLists?.results?.find(
        (l: MedScout.List) => l.id.toString() === listId
      );
      return list?.name ?? '';
    },
    [providerLists, newListName]
  );

  // Reset state
  const resetState = useCallback(() => {
    setSelectedListId(null);
    setIsCreatingList(false);
    setNewListName('');
    setSearchTerm('');
    setProgress({ current: 0, total: 0 });
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Handle selection
  const handleSelection = useCallback((listId: string) => {
    setSelectedListId(listId);
    setIsCreatingList(false);
  }, []);

  // Handle new list button
  const handleAddList = useCallback(() => {
    setIsCreatingList(true);
    setSelectedListId('new-list-id');
  }, []);

  // Handle cancel create
  const handleCancelCreate = useCallback(() => {
    setIsCreatingList(false);
    setNewListName('');
    setSelectedListId(null);
  }, []);

  // Handle create from search
  const handleCreateFromSearch = useCallback(() => {
    setNewListName(debouncedSearchTerm.trim());
    setIsCreatingList(true);
    setSelectedListId('new-list-id');
  }, [debouncedSearchTerm]);

  // Handle add operation
  const handleAdd = useCallback(async () => {
    if (!selectedListId || !normalizedProviders.length) return;

    try {
      const providersToAdd = filterDuplicates(selectedListId);
      const skippedCount = normalizedProviders.length - providersToAdd.length;

      if (providersToAdd.length === 0) {
        setNotification({
          title: 'No providers added',
          message: 'All selected providers are already in the list.',
          type: 'info',
        });
        handleClose();
        return;
      }

      await addItemsToList({
        listId: selectedListId,
        items: providersToAdd.map(({ provider_id, type }) => ({
          provider_id,
          type,
        })),
      }).unwrap();

      const result: AddToListResult = {
        targetListId: selectedListId,
        targetListName: getSelectedListName(selectedListId),
        addedCount: providersToAdd.length,
        skippedDuplicates: skippedCount,
        isNewList: false,
      };

      log.event('addProvider', {
        source: 'addToListModal',
        mode: 'add',
        count: providersToAdd.length,
      });

      setNotification({
        title: 'Providers added',
        message: `Added ${providersToAdd.length} ${
          providersToAdd.length === 1 ? 'provider' : 'providers'
        } to the list.`,
        type: 'success',
      });

      onSuccess?.(result);
      handleClose();
    } catch (err: any) {
      log.exception('Error adding providers to list', err);
      setNotification({
        title: 'Error',
        message:
          err?.error_message ||
          'An error occurred while adding providers to the list.',
        type: 'error',
      });
    }
  }, [
    selectedListId,
    normalizedProviders,
    filterDuplicates,
    addItemsToList,
    getSelectedListName,
    log,
    setNotification,
    onSuccess,
    handleClose,
  ]);

  // Handle move operation
  const handleMove = useCallback(async () => {
    if (!selectedListId || !normalizedProviders.length) return;

    if (!sourceList?.id) {
      setNotification({
        title: 'Error',
        message: 'No source list found',
        type: 'error',
      });
      return;
    }

    try {
      const providersToMove = filterDuplicates(selectedListId);
      const skippedCount = normalizedProviders.length - providersToMove.length;

      if (providersToMove.length === 0) {
        setNotification({
          title: 'No providers to move',
          message: 'All selected providers are already in the target list.',
          type: 'info',
        });
        handleClose();
        return;
      }

      if (skippedCount > 0) {
        setNotification({
          title: 'Duplicates skipped',
          message: `${skippedCount} ${
            skippedCount === 1 ? 'provider is' : 'providers are'
          } already in the target list.`,
          type: 'info',
        });
      }

      // Step 1: Delete from source list
      const total = providersToMove.length;
      setProgress({ current: 0, total });

      for (let i = 0; i < providersToMove.length; i++) {
        const provider = providersToMove[i];
        setProgress({ current: i + 1, total });

        if (provider.list_item_id) {
          await deleteListItem({
            listId: String(sourceList.id),
            itemId: provider.list_item_id,
          }).unwrap();
        }
      }

      // Step 2: Add to target list
      await addItemsToList({
        listId: selectedListId,
        items: providersToMove.map(({ provider_id, type }) => ({
          provider_id,
          type,
        })),
      }).unwrap();

      const result: AddToListResult = {
        targetListId: selectedListId,
        targetListName: getSelectedListName(selectedListId),
        addedCount: providersToMove.length,
        skippedDuplicates: skippedCount,
        isNewList: false,
      };

      log.event('moveProviders', {
        sourceListId: sourceList.id,
        targetListId: selectedListId,
        count: total,
      });

      setNotification({
        title: 'Success',
        message: `Moved ${total} ${
          total === 1 ? 'provider' : 'providers'
        } to list`,
        type: 'success',
      });

      triggerListMembersRefetch();
      onSuccess?.(result);
      handleClose();
    } catch (err: any) {
      log.exception('Error moving providers to list', err);
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Failed to move items to list',
        type: 'error',
      });
      setProgress({ current: 0, total: 0 });
    }
  }, [
    selectedListId,
    normalizedProviders,
    sourceList,
    filterDuplicates,
    deleteListItem,
    addItemsToList,
    getSelectedListName,
    log,
    setNotification,
    triggerListMembersRefetch,
    onSuccess,
    handleClose,
  ]);

  // Handle create and add/move
  const handleCreateAndSubmit = useCallback(async () => {
    if (!newListName.trim()) {
      setNotification({
        title: 'Error',
        message: 'Please enter a list name',
        type: 'error',
      });
      return;
    }

    const nameExists = providerLists?.results?.some(
      (list: MedScout.List) =>
        list.name.toLowerCase() === newListName.trim().toLowerCase()
    );
    if (nameExists) {
      setNotification({
        title: 'Error',
        message: `A list named "${newListName.trim()}" already exists`,
        type: 'error',
      });
      return;
    }

    try {
      const createdList = await createList({ listName: newListName }).unwrap();
      const newListId = createdList.id.toString();

      log.event('List created', { name: newListName, fromModal: mode });

      // For add mode, just add to the new list
      if (mode === 'add') {
        await addItemsToList({
          listId: newListId,
          items: normalizedProviders.map(({ provider_id, type }) => ({
            provider_id,
            type,
          })),
        }).unwrap();

        const result: AddToListResult = {
          targetListId: newListId,
          targetListName: newListName,
          addedCount: normalizedProviders.length,
          skippedDuplicates: 0,
          isNewList: true,
        };

        setNotification({
          title: 'Success',
          message: `Created list "${newListName}" and added ${
            normalizedProviders.length
          } ${normalizedProviders.length === 1 ? 'provider' : 'providers'}`,
          type: 'success',
        });

        onSuccess?.(result);
        handleClose();
        return;
      }

      // For move mode, need to delete from source first
      if (!sourceList?.id) {
        setNotification({
          title: 'Error',
          message: 'No source list found',
          type: 'error',
        });
        return;
      }

      const total = normalizedProviders.length;
      setProgress({ current: 0, total });

      for (let i = 0; i < normalizedProviders.length; i++) {
        const provider = normalizedProviders[i];
        setProgress({ current: i + 1, total });

        if (provider.list_item_id) {
          await deleteListItem({
            listId: String(sourceList.id),
            itemId: provider.list_item_id,
          }).unwrap();
        }
      }

      await addItemsToList({
        listId: newListId,
        items: normalizedProviders.map(({ provider_id, type }) => ({
          provider_id,
          type,
        })),
      }).unwrap();

      const result: AddToListResult = {
        targetListId: newListId,
        targetListName: newListName,
        addedCount: normalizedProviders.length,
        skippedDuplicates: 0,
        isNewList: true,
      };

      setNotification({
        title: 'Success',
        message: `Created list "${newListName}" and moved ${total} ${
          total === 1 ? 'provider' : 'providers'
        }`,
        type: 'success',
      });

      triggerListMembersRefetch();
      onSuccess?.(result);
      handleClose();
    } catch (err: any) {
      log.exception('Error creating list', err);
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Failed to create list',
        type: 'error',
      });
      setProgress({ current: 0, total: 0 });
    }
  }, [
    newListName,
    providerLists,
    mode,
    normalizedProviders,
    sourceList,
    createList,
    addItemsToList,
    deleteListItem,
    log,
    setNotification,
    triggerListMembersRefetch,
    onSuccess,
    handleClose,
  ]);

  // Handle submit based on mode and state
  const handleSubmit = useCallback(() => {
    if (isCreatingList) {
      handleCreateAndSubmit();
    } else if (mode === 'add') {
      handleAdd();
    } else {
      handleMove();
    }
  }, [isCreatingList, mode, handleCreateAndSubmit, handleAdd, handleMove]);

  // Button text
  const getSubmitButtonText = () => {
    if (isCreatingList) {
      return mode === 'add' ? 'Create List and Add' : 'Create and Move';
    }
    return mode === 'add' ? 'Add to List' : 'Move';
  };

  const canSubmit =
    selectedListId &&
    (isCreatingList ? newListName.trim() && !nameAlreadyExists : true);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      disableClickOutside={isProcessing}
      data-testid="add-to-list-modal"
    >
      <Dialog.Header>
        <div className="flex items-center justify-between w-full">
          <h2 className="text-xl font-semibold">
            {mode === 'add' ? 'Add to List' : 'Move to List'}
          </h2>
          {!isCreatingList && (
            <PulseButton
              variant="plain"
              color="primary"
              onClick={handleAddList}
              startDecorator={faPlus}
              disabled={isProcessing}
              data-testid="new-list-button"
            >
              New List
            </PulseButton>
          )}
        </div>
      </Dialog.Header>
      <Dialog.Content className="p-6">
        <div className="flex flex-col gap-3 min-h-[300px]">
          {/* Resolving the full "select all" set before it can be submitted */}
          {isResolving && (
            <div
              role="status"
              aria-live="polite"
              className="flex items-center gap-2 p-4 bg-sky-50 border border-sky-200 rounded-lg"
              data-testid="resolving-selection-notice"
            >
              <CircularProgress size="sm" color="primary" />
              <span className="text-sm font-medium text-sky-900">
                Gathering all selected providers…
              </span>
            </div>
          )}

          {/* Progress bar for move operations */}
          {progress.total > 0 && (
            <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-sky-900">
                  Removing from current list... {progress.current} of{' '}
                  {progress.total}
                </span>
                <span className="text-xs text-sky-600">
                  {Math.round((progress.current / progress.total) * 100)}%
                </span>
              </div>
              <div className="w-full bg-sky-200 rounded-full h-2">
                <div
                  className="bg-sky-600 h-2 rounded-full transition-all duration-300"
                  data-width={`${(progress.current / progress.total) * 100}%`}
                />
              </div>
            </div>
          )}

          {/* Search input */}
          <Search
            autoFocus
            onSearch={setSearchTerm}
            placeholder="Search Lists..."
            className="rounded-full w-full"
            data-testid="list-search-input"
          />

          {/* List selection */}
          {providerListsLoading ? (
            <div className="flex justify-center items-center py-12">
              <CircularProgress size="md" color="primary" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {/* Create new list input — top of list area */}
              {isCreatingList && (
                <div className="pb-2">
                  <CreateListInput
                    value={newListName}
                    onChange={setNewListName}
                    onCancel={handleCancelCreate}
                  />
                  {nameAlreadyExists && (
                    <p
                      className="px-3 pt-1 text-xs text-red-500"
                      data-testid="duplicate-list-name-error"
                    >
                      A list with this name already exists
                    </p>
                  )}
                </div>
              )}

              {hasNoSearchResults ? (
                /* Empty search state — hide list sections, show create */
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <p className="text-sm text-neutral-500">
                    No lists matching &ldquo;{debouncedSearchTerm.trim()}&rdquo;
                  </p>
                  {!isCreatingList && (
                    <button
                      type="button"
                      onClick={handleCreateFromSearch}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-full transition-colors cursor-pointer"
                      data-testid="create-from-search-button"
                    >
                      <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                      <span>Create &ldquo;{debouncedSearchTerm.trim()}&rdquo;</span>
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <ListSelector
                    title="My Lists"
                    lists={groupedLists.owned}
                    selectedId={selectedListId}
                    onSelect={handleSelection}
                  />
                  <ListSelector
                    title="Shared Lists"
                    lists={groupedLists.shared}
                    selectedId={selectedListId}
                    onSelect={handleSelection}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          onClick={handleClose}
          className="rounded-full"
          disabled={isProcessing}
          data-testid="cancel-button"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || isProcessing || isResolving}
          className="rounded-full"
          data-testid="submit-button"
        >
          {isProcessing || isResolving ? (
            <CircularProgress size="sm" color="neutral" />
          ) : (
            getSubmitButtonText()
          )}
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export default AddToListModal;
